/* eslint-disable no-useless-catch */
import ElementUI from 'element-ui'

export default {
  methods: {
    /**
     * Handle errors in components with consistent error handling
     * @param {Error} error - The error object
     * @param {Object} context - Additional context
     */
    handleError (error, context = {}) {
      const componentContext = {
        component: this.$options.name || 'UnknownComponent',
        ...context
      }

      return this.$errorService.handleError(error, componentContext)
    },

    /**
     * Enhanced error handling for API calls with retry functionality
     * @param {Function} apiCall - The API function to call
     * @param {Object} context - Context for error handling
     * @param {Function} onSuccess - Success callback
     * @param {Function} onError - Error callback
     * @param {number} maxRetries - Maximum number of retries
     */
    async handleApiCall (apiCall, context = {}, onSuccess = null, onError = null, maxRetries = 1) {
      let retries = 0

      const attemptCall = async () => {
        try {
          const result = await apiCall()
          if (onSuccess) {
            onSuccess(result)
          }
          return result
        } catch (error) {
          retries++

          if (retries <= maxRetries && this.shouldRetry(error)) {
            // Wait before retrying (exponential backoff)
            await this.delay(Math.pow(2, retries) * 1000)
            return attemptCall()
          }

          // Final error handling
          await this.handleError(error, {
            ...context,
            retries: retries - 1,
            maxRetries
          })

          if (onError) {
            onError(error)
          }

          throw error
        }
      }

      return attemptCall()
    },

    /**
     * Determine if an error should be retried
     * @param {Error} error - The error object
     * @returns {boolean} - Whether to retry
     */
    shouldRetry (error) {
      // Retry network errors and 5xx server errors
      return error.message === 'Network Error' ||
             (error.response && error.response.status >= 500 && error.response.status < 600)
    },

    /**
     * Delay utility for retry logic
     * @param {number} ms - Milliseconds to delay
     * @returns {Promise} - Promise that resolves after delay
     */
    delay (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    /**
     * Enhanced form validation error handling
     * @param {Error} error - The validation error
     * @param {Object} formRef - The form reference
     */
    handleValidationError (error, formRef = null) {
      if (error.response && error.response.status === 422) {
        const validationErrors = error.response.data

        // Clear previous validation errors
        if (formRef && this.$refs[formRef]) {
          this.$refs[formRef].clearValidate()
        }

        // Set field-specific validation errors
        if (typeof validationErrors === 'object') {
          Object.keys(validationErrors).forEach(field => {
            try {
              const messages = Array.isArray(validationErrors[field])
                ? validationErrors[field]
                : [validationErrors[field]]

              // Set validation error on form field
              if (formRef && this.$refs[formRef]) {
                this.$refs[formRef].validateField(field, () => {
                  // Field validation callback
                })
              }

              // Show notification for each error
              messages.forEach(message => {
                try {
                  ElementUI.Notification({
                    title: this.$t('error.validation.title'),
                    message: `${this.formatFieldName(field)}: ${message}`,
                    type: 'error',
                    duration: 5000,
                    customClass: 'error-notification'
                  })
                } catch (error) {
                  console.error('Error showing validation notification:', error)
                }
              })
            } catch (error) {
              console.error('Error processing validation field:', error)
            }
          })
        }
      } else {
        // Handle non-validation errors
        this.handleError(error, {
          action: 'form-validation',
          formRef: formRef
        })
      }
    },

    /**
     * Format field names for better display
     * @param {string} field - The field name
     * @returns {string} - Formatted field name
     */
    formatFieldName (field) {
      return field
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    },

    /**
     * Show loading state during API calls
     * @param {Function} apiCall - The API function
     * @param {string} loadingKey - The loading state key
     * @param {Object} context - Error context
     * @returns {Promise} - Promise that resolves with the result
     */
    async withLoading (apiCall, loadingKey = 'loading', context = {}) {
      this[loadingKey] = true

      try {
        const result = await apiCall()
        return result
      } catch (error) {
        // Error is already handled by the global axios interceptor
        // No need to handle it again here
        // eslint-disable-next-line no-useless-catch
        throw error
      } finally {
        this[loadingKey] = false
      }
    },

    /**
     * Show success notification
     * @param {string} message - Success message
     * @param {string} title - Success title
     */
    showSuccess (message, title = null) {
      ElementUI.Notification({
        title: title || this.$t('status.success'),
        message: message,
        type: 'success',
        duration: 3000
      })
    },

    /**
     * Show warning notification
     * @param {string} message - Warning message
     * @param {string} title - Warning title
     */
    showWarning (message, title = null) {
      ElementUI.Notification({
        title: title || this.$t('status.warning'),
        message: message,
        type: 'warning',
        duration: 4000,
        customClass: 'error-notification'
      })
    },

    /**
     * Show info notification
     * @param {string} message - Info message
     * @param {string} title - Info title
     */
    showInfo (message, title = null) {
      ElementUI.Notification({
        title: title || this.$t('status.info'),
        message: message,
        type: 'info',
        duration: 3000
      })
    }
  }
}
